import { BaseApi, ApiResponse } from "../utils/request";
import type { PageData } from "./records";

/**
 * 红包雨（RED_ENVELOPE_RAIN，新活动；与老 RED_PACKET_RAIN 无关）前台接口
 * 契约基准：.wt/rer-spec.md §4（后端 RedEnvelopeRainController，`v1/red_envelope_rain`）
 * 全部 POST + 需登录 + alert:false（错误由弹窗自行提示，不走全局弹窗）。
 *
 * 后端与本端并行开发：所有字段一律按「可缺省」声明，消费方必须做兜底（缺失不崩、不显示错乱）。
 */

/** 时间段状态（后端按请求时刻算的快照；前端以 startMs / endMs + 服务器时间偏移重新计算为准） */
export type RedEnvelopeRainSlotState = "PAST" | "CURRENT" | "FUTURE";

/** status.slots / nextDaySlots 元素 */
export interface RedEnvelopeRainSlot {
  /** HHmm（开始时间去冒号），同一天内唯一 */
  key?: string | null;
  /** HH:mm（站点时区，后端已格式化） */
  start?: string | null;
  /** HH:mm（可能为 24:00） */
  end?: string | null;
  /** 本场绝对开始时间（毫秒） */
  startMs?: number | null;
  /** 本场绝对结束时间（毫秒，开区间） */
  endMs?: number | null;
  state?: RedEnvelopeRainSlotState | string | null;
  /** 本人本场是否已占用名额（成功 / 部分 / 处理中） */
  claimed?: boolean | null;
}

/** 占位符取值（§10.4）：金额类为数字，slots / validity 为后端格式化好的字符串 */
export interface RedEnvelopeRainPlaceholders {
  capShow?: number | null;
  waveShow?: number | null;
  slotCount?: number | null;
  slots?: string | null;
  dailyTimes?: number | null;
  audit?: number | null;
  minDeposit?: number | null;
  minBet?: number | null;
  validity?: string | null;
}

/** 零 IO 可判的阻断（TERMINAL_NOT_SUPPORT > DAILY_LIMIT_REACHED > WAVE_EXHAUSTED） */
export interface RedEnvelopeRainBlocked {
  code?: string | null;
  params?: Record<string, unknown> | null;
}

/** /status 返回（ApiModelResp.model） */
export interface RedEnvelopeRainStatus {
  /** false = 不打开弹窗（已打开则关闭）并 toast reason；缺省视为 false */
  show?: boolean | null;
  reason?: string | null;
  activityId?: string | null;
  activityName?: string | null;
  /** 标题 / 描述 / 规则：支持 $xxx 占位符 */
  title?: string | null;
  brief?: string | null;
  rules?: string | null;
  headerImageUrl?: string | null;
  placeholders?: RedEnvelopeRainPlaceholders | null;
  /** 服务器当前时间（毫秒），前端据此算时钟偏移 */
  serverTime?: number | null;
  /** 今天（站点时区）yyyyMMdd */
  date?: string | null;
  /** 今天（站点时区）最后一毫秒 */
  dayEndMs?: number | null;
  slots?: RedEnvelopeRainSlot[] | null;
  nextDaySlots?: RedEnvelopeRainSlot[] | null;
  currentSlotKey?: string | null;
  dailyTimes?: number | null;
  claimCount?: number | null;
  /** 今日已领金额（活动页「今日收益」；2026-10-14 后端新增，旧后端缺省 → 按 0） */
  todayPaid?: number | null;
  /** 活动结束时刻（毫秒；null = 永久，活动页倒计时用；2026-10-14 后端新增） */
  activityEndMs?: number | null;
  /** 当前所在场的奖池是否已抢完（只对 currentSlotKey 那一场有效） */
  currentSlotWaveExhausted?: boolean | null;
  /** 本次请求被识别的终端 APP / PWA / BROWSER */
  terminal?: string | null;
  allowedTerminals?: string[] | null;
  blocked?: RedEnvelopeRainBlocked | null;
}

/** /claim 结果状态（REJECTED = 不落记录的拒绝） */
export type RedEnvelopeRainClaimStatus =
  | "SUCCESS"
  | "PARTIAL"
  | "FAILED"
  | "PROCESSING"
  | "REJECTED";

/** /claim 返回（ApiModelResp.model） */
export interface RedEnvelopeRainClaimResult {
  status?: RedEnvelopeRainClaimStatus | string | null;
  slotKey?: string | null;
  paid?: number | null;
  audit?: number | null;
  failReason?: string | null;
  failParams?: Record<string, unknown> | null;
  recordId?: string | null;
}

/** /records 单条（本人记录；MANUAL_REVIEW 已由后端转成 PROCESSING 且不带原因） */
export interface RedEnvelopeRainRecord {
  id?: string;
  created?: number;
  date?: string;
  slotKey?: string;
  slotStart?: number;
  activityName?: string;
  status?: string;
  failReason?: string | null;
  failParams?: Record<string, unknown> | null;
  calcAmount?: number | null;
  paid?: number | null;
  audit?: number | null;
}

/** /records 入参：size ≤ 50；days 0 / 不传 = 全部 */
export interface RedEnvelopeRainRecordsParams {
  page: number;
  size: number;
  days?: number;
}

/** /records 返回（ApiPageQueryResp，没有 model 字段） */
export interface RedEnvelopeRainRecordsResp {
  page?: PageData<RedEnvelopeRainRecord> | null;
}

export class RedEnvelopeRainApi extends BaseApi {
  /**
   * 弹窗状态。activityId 可选：与当日锁定活动不一致时后端按锁定活动返回。
   * 不开全局 in-flight 去重：公共去重键不含用户维度（账号切换会复用在途响应），
   * 30 秒复用与身份校验由调用方（statusCache.ts）负责。
   */
  async status(
    activityId?: string
  ): Promise<ApiResponse<RedEnvelopeRainStatus, true>> {
    const body: Record<string, unknown> = {};
    if (activityId) body.activityId = activityId;
    return this.post<RedEnvelopeRainStatus, true>(
      "/api/v1/red_envelope_rain/status",
      body,
      { alert: false }
    );
  }

  /** 领取：只传 activityId（时段由服务端按当前时刻判定）。写接口不开 dedupe，防连点由弹窗同步锁负责 */
  async claim(
    activityId: string
  ): Promise<ApiResponse<RedEnvelopeRainClaimResult, true>> {
    return this.post<RedEnvelopeRainClaimResult, true>(
      "/api/v1/red_envelope_rain/claim",
      { activityId },
      { alert: false }
    );
  }

  /** 本人领取记录（P1 app 不做专属记录页，接口留给后续与 QA 使用） */
  async records(
    params: RedEnvelopeRainRecordsParams
  ): Promise<ApiResponse<RedEnvelopeRainRecordsResp>> {
    const body: Record<string, unknown> = {
      page: params.page,
      size: params.size,
    };
    if (params.days) body.days = params.days;
    return this.post<RedEnvelopeRainRecordsResp>(
      "/api/v1/red_envelope_rain/records",
      body,
      { alert: false }
    );
  }
}

export const redEnvelopeRainApi = new RedEnvelopeRainApi();

/** 取 status 的 model；请求异常 / 无 model 返回 null（调用方按「未知失败」兜底） */
export async function getRedEnvelopeRainStatus(
  activityId?: string
): Promise<RedEnvelopeRainStatus | null> {
  const resp = await redEnvelopeRainApi.status(activityId);
  return resp?.data?.model ?? null;
}

/** 取 claim 的 model；无 model 返回 null（调用方按「未知失败」兜底并重拉 status） */
export async function claimRedEnvelopeRain(
  activityId: string
): Promise<RedEnvelopeRainClaimResult | null> {
  const resp = await redEnvelopeRainApi.claim(activityId);
  return resp?.data?.model ?? null;
}

/** 取 records 的分页数据；无数据返回 null */
export async function getRedEnvelopeRainRecords(
  params: RedEnvelopeRainRecordsParams
): Promise<PageData<RedEnvelopeRainRecord> | null> {
  const resp = await redEnvelopeRainApi.records(params);
  return resp?.data?.page ?? null;
}
